import {
  NewFormTextField,
  NewRegionFormSelectField,
} from '../../../presentation';
import {
  Box,
  FormControlLabel,
  Paper,
  Radio,
  RadioGroup,
  Stack,
} from '@mui/material';
import {
  Controller,
  FormProvider,
  useForm,
  useFormState,
} from 'react-hook-form';
import { forwardRef, useEffect, useImperativeHandle, useRef } from 'react';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import './underwriting.submission.surplusLine.scss';

const surplusLineSchema = yup.object({
  licenseeBrokerageName: yup
    .string()
    .trim()
    .required('Licensee Brokerage Name is required'),
  licenseNumber: yup.string().trim().required('License Number is required'),
});

export interface SurplusLineHandle {
  triggerValidation: () => Promise<boolean>;
  getValues: () => Record<string, unknown>;
  reset: (values: Record<string, unknown>) => void;
  isDisabled: () => boolean;
}

const DEFAULT_VALUES = {
  licenseType: '',
  licenseeBrokerageName: '',
  licenseeFirstName: '',
  licenseeLastName: '',
  licenseNumber: '',
  address1: '',
  address2: '',
  city: '',
  state: '',
  postalCode: '',
  licensePhone: '',
  licensePhoneExt: '',
  transactionalIdNumber: '',
};

interface SurplusLineProps {
  onValidityChange?: (isValid: boolean) => void;
  onFormChange?: () => void;
  disabled?: boolean;
  clearOnDisable?: boolean;
  attached?: boolean;
  initialValues?: Record<string, unknown>;
}

export const UnderwritingSubmissionSurplusLine = forwardRef<
  SurplusLineHandle,
  SurplusLineProps
>(
  (
    {
      onValidityChange,
      onFormChange,
      disabled = false,
      clearOnDisable = false,
      attached = false,
      initialValues,
    },
    ref
  ) => {
    const form = useForm({
      resolver: yupResolver(surplusLineSchema),
      mode: 'onChange',
      defaultValues: DEFAULT_VALUES,
    });

    const { isValid } = useFormState({ control: form.control });
    const isProgrammaticReset = useRef(false);

    const doReset = (values: Record<string, unknown>) => {
      isProgrammaticReset.current = true;
      form.reset(values);
    };

    useImperativeHandle(ref, () => ({
      triggerValidation: () => form.trigger(),
      getValues: () => form.getValues(),
      reset: (values) => doReset(values),
      isDisabled: () => disabled,
    }));

    useEffect(() => {
      if (initialValues) {
        doReset({ ...DEFAULT_VALUES, ...initialValues });
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [initialValues]);

    useEffect(() => {
      const subscription = form.watch(() => {
        if (isProgrammaticReset.current) {
          isProgrammaticReset.current = false;
          return;
        }
        onFormChange?.();
      });
      return () => subscription.unsubscribe();
    }, [form, onFormChange]);

    useEffect(() => {
      if (disabled) {
        if (clearOnDisable) {
          doReset(DEFAULT_VALUES);
        }
        onValidityChange?.(true);
      } else {
        onValidityChange?.(isValid);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [disabled, clearOnDisable, isValid, onValidityChange]);

    return (
      <>
        <Stack
          sx={
            attached
              ? {
                  boxShadow: 'none',
                  border: '1px solid rgba(0, 0, 0, 0.12)',
                  borderRadius: '0 0 4px 4px',
                  marginBottom: '20px',
                  padding: '0 25px',
                }
              : undefined
          }
        >
          <Stack flexDirection={'row'} gap={2}>
            <Box className="surplus_line">
              <Box className="main-title">
                Surplus Lines Tax Filling Information
              </Box>
              <Box className="subtitle">
                This information should be taken from the Surplus Lines Tax
                Filing confirmation form.
              </Box>
            </Box>
          </Stack>
          <Stack style={{ width: '100%' }}>
            <Box className="surplus_line">
              <Paper elevation={0}>
                <FormProvider {...form}>
                  <Box>
                    <Box
                      className="surplus-container"
                      sx={{ border: 'none', padding: 0, margin: 0 }}
                    >
                      <Box className="row-data">
                        <Box className="surplus-row-field">
                          <Controller
                            name="licenseeBrokerageName"
                            control={form.control}
                            render={({ field }) => (
                              <NewFormTextField
                                {...field}
                                required
                                disabled={disabled}
                                label="LICENSEE BROKERAGE NAME"
                                placeholder="Enter Licensee Brokerage Name"
                                data-testid="licensee-brokerage-name-input"
                              />
                            )}
                          />
                        </Box>
                        <Box className="surplus-row-field">
                          <Controller
                            name="licenseeFirstName"
                            control={form.control}
                            render={({ field }) => (
                              <NewFormTextField
                                {...field}
                                disabled={disabled}
                                label="LICENSEE FIRST NAME"
                                placeholder="Enter Licensee First Name"
                                data-testid="licensee-first-name-input"
                              />
                            )}
                          />
                        </Box>
                        <Box className="surplus-row-field">
                          <Controller
                            name="licenseeLastName"
                            control={form.control}
                            render={({ field }) => (
                              <NewFormTextField
                                {...field}
                                disabled={disabled}
                                label="LICENSEE LAST NAME"
                                placeholder="Enter Licensee Last Name"
                                data-testid="licensee-last-name-input"
                              />
                            )}
                          />
                        </Box>
                      </Box>

                      <Box className="row-data">
                        <Box className="surplus-row-field">
                          <Controller
                            name="address1"
                            control={form.control}
                            render={({ field }) => (
                              <NewFormTextField
                                {...field}
                                disabled={disabled}
                                label="ADDRESS 1"
                                placeholder="1234 Main St"
                                data-testid="address1-input"
                              />
                            )}
                          />
                        </Box>
                        <Box className="surplus-row-field">
                          <Controller
                            name="address2"
                            control={form.control}
                            render={({ field }) => (
                              <NewFormTextField
                                {...field}
                                disabled={disabled}
                                label="ADDRESS 2"
                                placeholder="Suite 123"
                                data-testid="address2-input"
                              />
                            )}
                          />
                        </Box>
                        <Box className="surplus-row-field">
                          <Controller
                            name="city"
                            control={form.control}
                            render={({ field }) => (
                              <NewFormTextField
                                {...field}
                                disabled={disabled}
                                label="CITY"
                                placeholder="Enter City"
                                data-testid="city-input"
                              />
                            )}
                          />
                        </Box>
                      </Box>

                      <Box className="row-data">
                        <Box className="surplus-row-field">
                          <NewRegionFormSelectField
                            name="state"
                            label="STATE"
                            placeholder="Select State"
                            disabled={disabled}
                            data-testid="state-input"
                          />
                        </Box>
                        <Box className="surplus-row-field">
                          <Controller
                            name="postalCode"
                            control={form.control}
                            render={({ field }) => (
                              <NewFormTextField
                                {...field}
                                disabled={disabled}
                                label="POSTAL/ZIP CODE"
                                placeholder="Enter Postal or ZIP Code"
                                data-testid="zip-code-input"
                              />
                            )}
                          />
                        </Box>
                        <Box className="surplus-row-field">
                          <Box className="surplus-phone-row">
                            <Controller
                              name="licensePhone"
                              control={form.control}
                              render={({ field }) => (
                                <NewFormTextField
                                  {...field}
                                  disabled={disabled}
                                  label="LICENSE BROKERAGE PHONE"
                                  placeholder="415-555-1212"
                                  data-testid="license-phone-input"
                                  isPhoneNumber
                                  className="surplus-phone-main"
                                />
                              )}
                            />
                            <Controller
                              name="licensePhoneExt"
                              control={form.control}
                              render={({ field }) => (
                                <NewFormTextField
                                  {...field}
                                  disabled={disabled}
                                  hiddenLabel
                                  placeholder="Ext"
                                  data-testid="license-phone-ext-input"
                                  className="surplus-phone-ext"
                                />
                              )}
                            />
                          </Box>
                        </Box>
                      </Box>

                      <Box className="row-data">
                        <Box className="surplus-row-field">
                          <Controller
                            name="licenseNumber"
                            control={form.control}
                            render={({ field }) => (
                              <NewFormTextField
                                {...field}
                                required
                                disabled={disabled}
                                label="LICENSE NUMBER"
                                placeholder="Enter License Number"
                                data-testid="license-number-input"
                              />
                            )}
                          />
                        </Box>
                        <Box className="surplus-row-field-padded">
                          <Box className="surplus-field-label">
                            LICENSE TYPE
                          </Box>
                          <Controller
                            name="licenseType"
                            control={form.control}
                            render={({ field }) => (
                              <RadioGroup
                                {...field}
                                row
                                sx={{
                                  flexDirection: 'row',
                                  flexWrap: 'nowrap',
                                }}
                              >
                                <FormControlLabel
                                  value="resident"
                                  disabled={disabled}
                                  control={
                                    <Radio
                                      className="radio-button unchecked"
                                      classes={{ checked: 'checked' }}
                                    />
                                  }
                                  label="Resident"
                                />
                                <FormControlLabel
                                  value="non-resident"
                                  disabled={disabled}
                                  control={
                                    <Radio
                                      className="radio-button unchecked"
                                      classes={{ checked: 'checked' }}
                                    />
                                  }
                                  label="Non-Resident"
                                />
                              </RadioGroup>
                            )}
                          />
                        </Box>
                        <Box className="surplus-row-field">
                          <Controller
                            name="transactionalIdNumber"
                            control={form.control}
                            render={({ field }) => (
                              <NewFormTextField
                                {...field}
                                disabled={disabled}
                                label="TRANSACTIONAL ID NUMBER"
                                placeholder="Enter only if Applicable"
                                data-testid="transactional-id-number-input"
                              />
                            )}
                          />
                        </Box>
                      </Box>
                    </Box>
                  </Box>
                </FormProvider>
              </Paper>
            </Box>
          </Stack>
        </Stack>
      </>
    );
  }
);
